import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Descriptions,
  Tag,
  Divider,
  Row,
  Col,
  Statistic,
  Table,
  Tooltip,
  Empty,
  Typography,
  Space,
  Grid,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import apiService from "../../services/api";
import ErrorBoundary from "../common/ErrorBoundary";
import { formatDateTime } from "../../utils/formatters";
import "./Profile.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const shortenUserAgent = (ua) => {
  if (!ua || !String(ua).trim()) return "—";
  const s = String(ua).trim();
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
};

const sessionStatusTag = (status) => {
  const map = {
    active: { label: "有效", color: "success" },
    inactive: { label: "已登出", color: "default" },
    expired: { label: "已过期", color: "warning" },
  };
  const m = map[status] || { label: status || "未知", color: "default" };
  return <Tag color={m.color}>{m.label}</Tag>;
};

const Profile = () => {
  const screens = useBreakpoint();
  /** 与布局 lg 断点对齐：窄屏/平板侧栏展开时单列+纵向标签，宽屏双列横向 */
  const descriptionsWide = !!screens.lg;
  const { user } = useSelector((state) => state.auth);
  const [passwordForm] = Form.useForm();
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginHistory, setLoginHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setHistoryLoading(true);
      try {
        const [meRes, histRes] = await Promise.all([
          apiService.auth.getMe(),
          apiService.auth.getLoginHistory({ limit: 30 }),
        ]);
        if (cancelled) return;
        setProfileData(meRes.data?.data || user);
        setLoginHistory(
          Array.isArray(histRes.data?.data) ? histRes.data.data : [],
        );
      } catch (err) {
        if (cancelled) return;
        if (!user || Object.keys(user).length === 0) {
          message.error("获取用户信息失败");
        }
        setProfileData(user);
        setLoginHistory([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHistoryLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleChangePassword = async (values) => {
    try {
      setChangingPassword(true);
      await apiService.auth.changePassword({
        old_password: values.old_password,
        new_password: values.new_password,
      });
      message.success("密码修改成功");
      passwordForm.resetFields();
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.error?.message ||
        "密码修改失败";
      message.error(errMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  const roleLabel = profileData?.role === "admin" ? "管理员" : "普通用户";
  const roleColor = profileData?.role === "admin" ? "red" : "blue";
  const statusLabel = profileData?.status === "active" ? "正常" : "禁用";
  const statusColor = profileData?.status === "active" ? "green" : "default";

  const historyColumns = useMemo(
    () => [
      {
        title: "登录时间",
        dataIndex: "created_at",
        key: "created_at",
        width: 168,
        render: (v) => formatDateTime(v) || "—",
      },
      {
        title: "IP",
        dataIndex: "ip_address",
        key: "ip_address",
        width: 130,
        ellipsis: true,
        responsive: ["md"],
        render: (v) => v || "—",
      },
      {
        title: "客户端",
        dataIndex: "user_agent",
        key: "user_agent",
        ellipsis: true,
        render: (v) => {
          const short = shortenUserAgent(v);
          return (
            <Tooltip title={v || "—"} placement="topLeft">
              <span className="profile-ua-cell">{short}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "会话状态",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (s) => sessionStatusTag(s),
      },
      {
        title: "最后活跃",
        dataIndex: "last_seen",
        key: "last_seen",
        width: 168,
        responsive: ["sm"],
        render: (v) => formatDateTime(v) || "—",
      },
      {
        title: "备注",
        key: "note",
        width: 100,
        render: (_, row) =>
          row.is_current ? (
            <Tag color="processing" icon={<DesktopOutlined />}>
              当前设备
            </Tag>
          ) : (
            "—"
          ),
      },
    ],
    [],
  );

  return (
    <ErrorBoundary>
      <div className="profile-container">
        <header className="profile-page-header">
          <Title level={2} className="profile-heading">
            个人中心
          </Title>
          <Text type="secondary" className="profile-subtitle">
            管理账号资料、查看近期登录记录与修改密码
          </Text>
        </header>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <Card title="基本信息" loading={loading} className="profile-card">
              <Descriptions
                column={descriptionsWide ? 2 : 1}
                layout={descriptionsWide ? "horizontal" : "vertical"}
                bordered
                className={`profile-descriptions${descriptionsWide ? "" : " profile-descriptions--stack"}`}
                size="middle"
              >
                <Descriptions.Item
                  label={
                    <span>
                      <UserOutlined /> 用户名
                    </span>
                  }
                >
                  {profileData?.username}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <span>
                      <MailOutlined /> 邮箱
                    </span>
                  }
                >
                  {profileData?.email || "未设置"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <span>
                      <SafetyOutlined /> 角色
                    </span>
                  }
                >
                  <Tag color={roleColor}>{roleLabel}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={statusColor}>{statusLabel}</Tag>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <span>
                      <ClockCircleOutlined /> 注册时间
                    </span>
                  }
                >
                  {formatDateTime(profileData?.created_at) || "未知"}
                </Descriptions.Item>
                <Descriptions.Item label="最近登录（账号）">
                  {formatDateTime(profileData?.last_login_at) || "从未"}
                </Descriptions.Item>
              </Descriptions>

              <Divider className="profile-divider-compact" />

              <Row gutter={[16, 16]} className="stats-section">
                <Col xs={24} sm={12}>
                  <Statistic
                    title="累计登录次数"
                    value={profileData?.login_count ?? 0}
                  />
                </Col>
                <Col xs={24} sm={12}>
                  <Statistic
                    title="账号状态"
                    value={profileData?.status === "active" ? "正常" : "禁用"}
                    valueStyle={{
                      color:
                        profileData?.status === "active"
                          ? "var(--color-success)"
                          : "var(--color-error)",
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card
              title={
                <Space>
                  <HistoryOutlined />
                  <span>最近登录记录</span>
                </Space>
              }
              className="profile-card profile-card--history"
              loading={historyLoading}
            >
              {!historyLoading && loginHistory.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="暂无会话记录（登录后会在此展示）"
                />
              ) : (
                <Table
                  className="profile-login-history-table"
                  size="small"
                  rowKey="id"
                  columns={historyColumns}
                  dataSource={loginHistory}
                  pagination={false}
                  scroll={{ x: 720 }}
                />
              )}
            </Card>
          </Col>
        </Row>

        <Card title="修改密码" className="profile-card">
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleChangePassword}
            className="password-form"
          >
            <Form.Item
              label="原密码"
              name="old_password"
              rules={[{ required: true, message: "请输入原密码" }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入原密码"
              />
            </Form.Item>

            <Form.Item
              label="新密码"
              name="new_password"
              rules={[
                { required: true, message: "请输入新密码" },
                { min: 6, message: "密码至少6位" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入新密码（至少6位）"
              />
            </Form.Item>

            <Form.Item
              label="确认新密码"
              name="confirm_password"
              dependencies={["new_password"]}
              rules={[
                { required: true, message: "请确认新密码" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("new_password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("两次输入的密码不一致"));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请再次输入新密码"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={changingPassword}
              >
                修改密码
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </ErrorBoundary>
  );
};

export default Profile;
